import React, { useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import blogPosts from '../data/blogPosts';
import './Blog.css';

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const ensureMeta = (name, content) => {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

const insertSchema = (post, url) => {
  const existing = document.getElementById('blog-article-schema');
  if (existing) existing.remove();

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.metaDescription || post.excerpt,
    datePublished: post.date,
    author: { '@type': 'Organization', name: post.author || 'Sapiencia.AI' },
    publisher: {
      '@type': 'Organization',
      name: 'Sapiencia.AI',
      logo: {
        '@type': 'ImageObject',
        url: `${window.location.origin}/no_backg_logo.jpeg`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
  };

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = 'blog-article-schema';
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);

  return () => script.remove();
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    if (!post) {
      navigate('/blog', { replace: true });
      return;
    }

    const title = `${post.title} | Sapiencia.AI`;
    const description = post.metaDescription || post.excerpt;
    document.title = title;
    ensureMeta('description', description);

    const cleanupSchema = insertSchema(post, `${window.location.origin}/blog/${post.slug}`);
    return () => {
      cleanupSchema();
    };
  }, [navigate, post]);

  if (!post) return null;

  return (
    <div className="blog-page">
      <Navbar />
      <main className="blog-main">
        <article className="blog-post">
          <header className="blog-post-header">
            <p className="eyebrow">Insights</p>
            <h1>{post.title}</h1>
            <div className="blog-post-meta">
              <span className="blog-card-category">{post.category}</span>
              <span>{formatDate(post.date)}</span>
              {post.readingTime && <span>{post.readingTime}</span>}
              {post.author && <span>By {post.author}</span>}
            </div>
            {post.tags?.length ? (
              <div className="blog-card-tags" aria-label="Tags">
                {post.tags.map((tag) => (
                  <span key={tag} className="blog-tag">#{tag}</span>
                ))}
              </div>
            ) : null}
          </header>

          <div className="blog-post-content">
            {post.content.map((block, idx) => {
              if (block.type === 'heading') {
                return <h2 key={idx}>{block.text}</h2>;
              }
              if (block.type === 'list') {
                return (
                  <ul key={idx}>
                    {block.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                );
              }
              return <p key={idx}>{block.text}</p>;
            })}
          </div>

          <div className="blog-post-backlink">
            <Link to="/blog" aria-label="Back to blog">← Back to Blog</Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
